/*
 * REGIONAL (F3) — billing country / Google Play billing region. NEVER member location.
 * Cells under the minimum are withheld (with secondary suppression); shown counts are rounded.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Globe2 } from "lucide-react";
import { Panel, Note, Chip, fmt, FreshnessNote } from "./ConsolePrimitives";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/founder/regional`;

const Cells = ({ block, labels, prefix }) => {
  if (!block) return null;
  if (block.all_withheld) return <Note testId={`${prefix}-all-withheld`}>Every cell is withheld — too few accounts to report safely.</Note>;
  return (
    <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
      {(block.cells || []).map((c) => (
        <Chip key={c.key} tone={c.kind === "country" ? "good" : "quiet"} testId={`${prefix}-${c.key}`}>
          {c.kind === "country" ? c.key : labels?.statuses?.[c.key] || c.key}: ~{fmt(c.count)}
        </Chip>
      ))}
      {block.withheld_pool && block.withheld_pool.count > 0 && (
        <Chip tone="quiet" testId={`${prefix}-pool`}>Other (withheld individually): ~{fmt(block.withheld_pool.count)}</Chip>
      )}
    </div>
  );
};

export const RegionalView = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(API);
      setData(res.data);
    } catch (e) {
      setError("Regional figures could not be read just now.");
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  const labels = data?.labels;

  return (
    <>
      <header className="fc__section-head">
        <h2 data-testid="fc-regional-title">Regional</h2>
        <p data-testid="fc-regional-basis">{labels?.basis}</p>
      </header>
      <Panel title="Premium members by billing country" icon={Globe2} testId="fc-regional-panel"
        sub={data?.enabled ? `Cells under ${data.minimum_accounts} accounts withheld · counts rounded to ${data.rounding}` : undefined}>
        {data && !data.enabled && <Note testId="fc-regional-disabled">{data.reason}</Note>}
        {data?.enabled && (
          <>
            <Cells block={data.paying_members} labels={labels} prefix="fc-regional-paying" />
            {(data.new_billing_accounts_by_month?.months || []).map((m) => (
              <div key={m.month} style={{ marginTop: 12 }} data-testid={`fc-regional-month-${m.month}`}>
                <b>{m.month}</b> — first billing record observed
                <Cells block={m} labels={labels} prefix={`fc-regional-month-${m.month}`} />
              </div>
            ))}
            {data.stripe_net_receipts && (
              <div style={{ marginTop: 12 }} data-testid="fc-regional-net-receipts">
                <p>Stripe net receipts{data.stripe_net_receipts.complete ? "" : " (incomplete — some refunds could not be matched)"}:</p>
                {Object.keys(data.stripe_net_receipts.by_country || {}).length === 0
                  ? <Note testId="fc-regional-net-none">No country meets the minimum yet.</Note>
                  : Object.entries(data.stripe_net_receipts.by_country).map(([c, amounts]) => (
                    <p key={c} data-testid={`fc-regional-net-${c}`}>{c}: {Object.entries(amounts).map(([cur, v]) => `~${fmt(v)} ${cur.toUpperCase()}`).join(" · ")}</p>
                  ))}
                <Note>Withheld countries: {fmt(data.stripe_net_receipts.withheld_countries)} · {data.stripe_net_receipts.basis}</Note>
              </div>
            )}
            <p style={{ marginTop: 12 }} data-testid="fc-regional-conflicts">
              Stripe/Play country conflicts: {data.provider_conflicts?.withheld ? "withheld" : `~${fmt(data.provider_conflicts?.count)}`}
            </p>
          </>
        )}
        {labels && (
          <div style={{ marginTop: 12 }}>
            <Note testId="fc-regional-location">{labels.location}</Note>
            <Note testId="fc-regional-apple">Apple: {labels.apple}</Note>
            <Note testId="fc-regional-free">Free members: {labels.free_members}</Note>
            <Note testId="fc-regional-revenue">Revenue: {labels.revenue}</Note>
            <Note>{labels.capture_start}</Note>
          </div>
        )}
        {error && <Note testId="fc-regional-error">{error}</Note>}
      </Panel>
      {data?.freshness && <FreshnessNote freshness={data.freshness} />}
    </>
  );
};
